<template>
  <component
    :is="mobileMode ? 'q-popup-proxy' : 'q-menu'"
    ref="responsiveMenu"
    v-model="dialogOpen"
    :breakpoint="!mobileMode ? breakpoint : 9999"
    :no-focus="!mobileMode"
    :no-parent-event="computeNoParentEvent"
    :no-refocus="computeNoRefocus"
    @hide="$emit('hide')"
    @show="$emit('show')"
  >
    <q-card :style="cardStyle">
      <q-toolbar v-if="computeShowToolbar">
        <q-btn
          v-if="mobileMode"
          v-close-popup
          round
          flat
          dense
          :icon="toolbarCloseIcon"
        />

        <page-toolbar-title v-if="toolbarTitle">
          {{ toolbarTitle }}
        </page-toolbar-title>

        <slot name="toolbar-actions" />
      </q-toolbar>

      <slot :mobile-mode="mobileMode" />
    </q-card>
  </component>
</template>

<script>
/**
 * ResponsiveMenu acts a switch between showing a menu on desktop and a dialog on mobile devices
 * It extends on the QPopupProxy which can have weird behaviour between different device forms
 *
 * Because of how PopupProxy chooses the appropriate type (dialog, menu) on mobile, we must force a massive
 *  breakpoint on mobile devices
 *
 * SLOTS:
 * - default
 * - toolbar-actions: append actions to the toolbar
 */
export default {
  props: {
    // Breakpoint before activating mobile mode
    breakpoint: {
      type: Number,
      default: 450
    },
    // Force dialog mode if platform mode is mobile
    forceOnMobile: {
      type: Boolean,
      default: false
    },
    // Toolbar title if toolbar is visible
    toolbarTitle: {
      type: String,
      default: null
    },
    // Show toolbar on specific device forms
    showToolbar: {
      type: [String, Boolean],
      default: 'mobile',
      validator: (val) => {
        return [false, true, 'mobile', 'desktop'].includes(val)
      }
    },
    // Close btn on toolbar
    toolbarCloseIcon: {
      type: String,
      default: 'mdi-close'
    },
    // append no-parent-event prop on specific device forms (disables menu automatically showing when parent item is clicked)
    noParentEvent: {
      type: [String, Boolean],
      default: false,
      validator: (val) => {
        return [false, true, 'mobile', 'desktop'].includes(val)
      }
    },
    // append no-refocus prop on specific device forms (don't refocus on the parent element when menu is closed)
    noRefocus: {
      type: [String, Boolean],
      default: false,
      validator: (val) => {
        return [false, true, 'mobile', 'desktop'].includes(val)
      }
    },
    // append CSS style to QCard
    cardStyle: {
      type: [Object, String]
    }
  },
  emits: ['hide', 'show'],
  data () {
    return {
      dialogOpen: false
    }
  },
  computed: {
    mobileMode () {
      return this.$q.screen.width < this.breakpoint || (this.forceOnMobile && this.$q.platform.is.mobile)
    },
    computeNoRefocus () {
      return this.computePropVal(this.noRefocus)
    },
    computeNoParentEvent () {
      return this.computePropVal(this.noParentEvent)
    },
    computeShowToolbar () {
      return this.computePropVal(this.showToolbar)
    }
  },
  methods: {
    computePropVal (val) {
      switch (val) {
        case 'mobile': return this.mobileMode
        case 'desktop': return !this.mobileMode
        case true: return true
        default: return false
      }
    },
    open () {
      this.dialogOpen = true
    },
    close () {
      this.dialogOpen = false
    },
    updatePosition () {
      if (this.$refs.responsiveMenu && this.$refs.responsiveMenu.updatePosition) {
        this.$refs.responsiveMenu.updatePosition()
      }
    }
  }
}
</script>
